// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Financeiros/PagamentoLote.tsx
//  Wizard de pagamento em lote (Sprint 2D - Tier 1) — 4 passos:
//    1. Seleção de contas (filtros + multi-select)
//    2. Estratégia (forma + conta + data)
//    3. Revisão (preview com KPIs e avisos)
//    4. Execução (confirma e baixa)
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle,
  Wallet, Building2, Calendar, Loader2, Sparkles,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/apiClient';
import {
  pagamentoLoteService, type ContaPagavel, type PreviewLote,
} from '@/features/financeiro/services/pagamentoLoteService';
import { cadastrosFinanceirosService } from '@/features/financeiro/services/cadastrosFinanceirosService';

const fmtCur = (v: number | null | undefined) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (s: string) => new Date(s).toLocaleDateString('pt-BR');

const today = () => new Date().toISOString().slice(0, 10);
const monthsAgo = (n: number) => {
  const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 10);
};
const monthsAhead = (n: number) => {
  const d = new Date(); d.setMonth(d.getMonth() + n); return d.toISOString().slice(0, 10);
};

// ── Component ────────────────────────────────────────────────────────────────

export default function PagamentoLote() {
  const { toast } = useToast();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Step 1
  const [contas, setContas] = useState<ContaPagavel[]>([]);
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
  const [filtros, setFiltros] = useState({
    dtVencInicio: monthsAgo(3),
    dtVencFim:    monthsAhead(3),
    status: 'abertas' as 'abertas' | 'vencidas' | 'futuras' | 'todas',
  });
  const [carregando, setCarregando] = useState(false);

  // Step 2
  const [contaCorrenteId, setContaCorrenteId] = useState<number | undefined>(undefined);
  const [formaPagamentoId, setFormaPagamentoId] = useState<number | undefined>(undefined);
  const [dtPagamento, setDtPagamento] = useState(today());
  const [contas2, setContas2] = useState<{ id: number; descricao: string; saldoAtual?: number }[]>([]);
  const [formasPag, setFormasPag] = useState<{ id: number; descricao: string }[]>([]);

  // Modo de execução: "manual" | "cnab" | "inter"
  const [modoExecucao, setModoExecucao] = useState<'manual' | 'cnab' | 'inter'>('manual');
  const [contaTemInter, setContaTemInter] = useState(false);

  // Detecta se a conta selecionada tem integração Inter configurada
  useEffect(() => {
    if (!contaCorrenteId) { setContaTemInter(false); return; }
    cadastrosFinanceirosService.getIntegracaoContaCorrente(contaCorrenteId)
      .then((cfg) => setContaTemInter(
        cfg.tipoIntegracao === 1 &&
        !!cfg.clientId && !!cfg.clientSecret &&
        !!cfg.localArquivoCrt && !!cfg.localArquivoKey,
      ))
      .catch(() => setContaTemInter(false));
  }, [contaCorrenteId]);

  // Step 3
  const [preview, setPreview] = useState<PreviewLote | null>(null);
  const [carregandoPreview, setCarregandoPreview] = useState(false);

  // Step 4
  const [executando, setExecutando] = useState(false);
  const [resultado, setResultado] = useState<{ processadas: number; totalPago: number } | null>(null);

  // ── Carrega combos ─────────────────────────────────────────────────────────

  useEffect(() => {
    cadastrosFinanceirosService.getContasCorrentesCombo()
      .then((r) => setContas2(r as unknown as { id: number; descricao: string }[]))
      .catch(() => undefined);
    cadastrosFinanceirosService.getFormasPagamentoCombo()
      .then((r) => {
        setFormasPag(r as unknown as { id: number; descricao: string }[]);
      })
      .catch(() => undefined);
  }, []);

  // ── Step 1: Buscar contas ──────────────────────────────────────────────────

  const buscarContas = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await pagamentoLoteService.buscar({
        dtVencInicio: filtros.dtVencInicio,
        dtVencFim:    filtros.dtVencFim,
        status:       filtros.status,
        limit:        500,
      });
      setContas(r);
      setSelecionadas(new Set());
    } catch (e) {
      toast({ title: 'Erro ao buscar contas', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setCarregando(false);
    }
  }, [filtros, toast]);

  useEffect(() => { buscarContas(); }, [buscarContas]);

  // ── Step 3: Carregar preview ───────────────────────────────────────────────

  const carregarPreview = useCallback(async () => {
    setCarregandoPreview(true);
    try {
      const r = await pagamentoLoteService.preview(Array.from(selecionadas), contaCorrenteId);
      setPreview(r);
    } catch (e) {
      toast({ title: 'Erro ao gerar preview', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setCarregandoPreview(false);
    }
  }, [selecionadas, contaCorrenteId, toast]);

  useEffect(() => {
    if (step === 3) carregarPreview();
  }, [step, carregarPreview]);

  // ── Step 4: Executar ───────────────────────────────────────────────────────

  const totalSelecionado = useMemo(
    () => contas.filter((c) => selecionadas.has(c.id)).reduce((s, c) => s + c.valorSaldo, 0),
    [contas, selecionadas],
  );

  const executar = useCallback(async () => {
    setExecutando(true);
    try {
      const ids = Array.from(selecionadas);

      if (modoExecucao === 'inter' && contaCorrenteId) {
        // Pagamento direto via API Inter
        const { apiClient } = await import('@/lib/apiClient');
        const { data } = await apiClient.post('/api/inter-pagamento/lote', {
          contaCorrenteId,
          contaPagarIds: ids,
          dtPagamento,
        });
        setResultado({
          processadas: data.qtdEnviados ?? 0,
          totalPago: data.valorTotal ?? totalSelecionado,
        });
        toast({
          title: 'Lote enviado ao Inter',
          description: `Lote ${data.idLote ?? ''} — ${data.qtdEnviados} enviado(s), ${data.qtdRejeitados} rejeitado(s).`,
        });
      } else if (modoExecucao === 'cnab' && contaCorrenteId) {
        // Gera arquivo CNAB 240 e baixa
        const { apiClient } = await import('@/lib/apiClient');
        const { data } = await apiClient.post('/api/cnab-remessa/gerar', {
          contaCorrenteId,
          dtPagamento,
          contaPagarIds: ids,
        });
        // Download direto do arquivo gerado
        const blob = new Blob([data.conteudo], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.nomeArquivo;
        a.click();
        URL.revokeObjectURL(url);
        setResultado({ processadas: data.qtdRegistros, totalPago: data.valorTotal });
        toast({ title: 'CNAB 240 gerado', description: `${data.qtdRegistros} pagamento(s) no arquivo.` });
      } else {
        // Modo manual (default)
        const r = await pagamentoLoteService.executar({
          ids, dtPagamento, formaPagamentoId, contaCorrenteId,
        });
        setResultado({ processadas: r.processadas, totalPago: r.totalPago });
        toast({ title: r.mensagem });
      }
    } catch (e) {
      toast({ title: 'Erro ao executar', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setExecutando(false);
    }
  }, [selecionadas, dtPagamento, formaPagamentoId, contaCorrenteId, modoExecucao, totalSelecionado, toast]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const toggleSelecao = (id: number) => {
    setSelecionadas((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selecionarTodos = () => {
    if (selecionadas.size === contas.length) setSelecionadas(new Set());
    else setSelecionadas(new Set(contas.map((c) => c.id)));
  };

  const reset = () => {
    setStep(1);
    setSelecionadas(new Set());
    setPreview(null);
    setResultado(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Wallet className="h-8 w-8" />
          Pagamento em Lote
        </h1>
        <p className="text-muted-foreground mt-1">
          Assistente de 4 passos para quitar várias contas a pagar de uma só vez.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center gap-2 md:gap-6 py-4">
        {[
          { num: 1, label: 'Seleção' },
          { num: 2, label: 'Estratégia' },
          { num: 3, label: 'Revisão' },
          { num: 4, label: 'Confirmação' },
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-colors ${
              step >= s.num ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}>
              {step > s.num ? <CheckCircle2 className="h-5 w-5" /> : s.num}
            </div>
            <span className={`hidden md:inline text-sm ${step === s.num ? 'font-semibold' : 'text-muted-foreground'}`}>
              {s.label}
            </span>
            {i < 3 && <div className={`w-6 md:w-12 h-0.5 ${step > s.num ? 'bg-primary' : 'bg-muted'}`} />}
          </div>
        ))}
      </div>

      {/* ── Passo 1: Seleção ─────────────────────────────────────────────── */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">1. Selecionar Contas a Pagar</CardTitle>
            <CardDescription>Filtre e marque as contas que farão parte deste lote.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Vencimento de</Label>
                <Input type="date" value={filtros.dtVencInicio}
                  onChange={(e) => setFiltros({ ...filtros, dtVencInicio: e.target.value })} className="h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Até</Label>
                <Input type="date" value={filtros.dtVencFim}
                  onChange={(e) => setFiltros({ ...filtros, dtVencFim: e.target.value })} className="h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={filtros.status}
                  onValueChange={(v: 'abertas' | 'vencidas' | 'futuras' | 'todas') => setFiltros({ ...filtros, status: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="abertas">Em aberto (todas)</SelectItem>
                    <SelectItem value="vencidas">Apenas vencidas</SelectItem>
                    <SelectItem value="futuras">Apenas futuras</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="outline" size="sm" onClick={buscarContas} disabled={carregando} className="w-full">
                  <Search className="h-3 w-3 mr-1" />Buscar
                </Button>
              </div>
            </div>

            <div className="border rounded-lg overflow-x-auto max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={contas.length > 0 && selecionadas.size === contas.length}
                        onCheckedChange={selecionarTodos}
                      />
                    </TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-center">Atraso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {carregando ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-6">
                      <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />Carregando...
                    </TableCell></TableRow>
                  ) : contas.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Nenhuma conta encontrada.
                    </TableCell></TableRow>
                  ) : (
                    contas.map((c) => (
                      <TableRow key={c.id} className={selecionadas.has(c.id) ? 'bg-muted/30' : ''}>
                        <TableCell>
                          <Checkbox checked={selecionadas.has(c.id)} onCheckedChange={() => toggleSelecao(c.id)} />
                        </TableCell>
                        <TableCell className="font-medium">
                          {c.nomeFornecedor}
                          {c.cnpjCpfFornecedor && (
                            <div className="text-xs text-muted-foreground">{c.cnpjCpfFornecedor}</div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{c.numDocumento}</TableCell>
                        <TableCell>{fmtDate(c.dtVencimento)}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{fmtCur(c.valorSaldo)}</TableCell>
                        <TableCell className="text-center">
                          {c.diasAtraso > 0
                            ? <Badge variant="destructive">{c.diasAtraso}d</Badge>
                            : <Badge variant="outline" className="text-green-700">No prazo</Badge>}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-between items-center pt-3 border-t">
              <div className="text-sm">
                <span className="text-muted-foreground">{selecionadas.size} de {contas.length} selecionadas — </span>
                <span className="font-bold text-lg tabular-nums">{fmtCur(totalSelecionado)}</span>
              </div>
              <Button onClick={() => setStep(2)} disabled={selecionadas.size === 0}>
                Próximo <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Passo 2: Estratégia ──────────────────────────────────────────── */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">2. Definir Estratégia de Pagamento</CardTitle>
            <CardDescription>Escolha conta de origem, forma de pagamento e data.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />Conta de Origem
                </Label>
                <Select value={contaCorrenteId?.toString() ?? ''} onValueChange={(v) => setContaCorrenteId(Number(v))}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {contas2.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.descricao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />Forma de Pagamento
                </Label>
                <Select value={formaPagamentoId?.toString() ?? ''} onValueChange={(v) => setFormaPagamentoId(Number(v))}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {formasPag.map((f) => (
                      <SelectItem key={f.id} value={f.id.toString()}>{f.descricao}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />Data do Pagamento
                </Label>
                <Input type="date" value={dtPagamento} onChange={(e) => setDtPagamento(e.target.value)} />
              </div>
            </div>

            {/* Modo de execução */}
            <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Modo de Execução</Label>
                {contaTemInter && <Badge variant="default" className="bg-orange-600">Inter API disponível</Badge>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button type="button"
                  onClick={() => setModoExecucao('manual')}
                  className={`p-3 border-2 rounded-lg text-left transition-colors ${
                    modoExecucao === 'manual' ? 'border-primary bg-primary/5' : 'border-muted'
                  }`}>
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />Baixa Manual
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Marca contas como pagas no sistema (não envia ao banco).</p>
                </button>

                <button type="button"
                  onClick={() => setModoExecucao('cnab')}
                  className={`p-3 border-2 rounded-lg text-left transition-colors ${
                    modoExecucao === 'cnab' ? 'border-primary bg-primary/5' : 'border-muted'
                  }`}>
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />Gerar CNAB 240
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Gera arquivo .rem para envio via internet banking.</p>
                </button>

                <button type="button"
                  onClick={() => contaTemInter && setModoExecucao('inter')}
                  disabled={!contaTemInter}
                  className={`p-3 border-2 rounded-lg text-left transition-colors ${
                    modoExecucao === 'inter' ? 'border-orange-600 bg-orange-50 dark:bg-orange-950/30' :
                    contaTemInter ? 'border-muted' : 'border-muted opacity-50 cursor-not-allowed'
                  }`}>
                  <div className="font-semibold text-sm flex items-center gap-2">
                    🏦 Inter API
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {contaTemInter
                      ? 'Pagamento direto via API do Banco Inter.'
                      : 'Configure Inter API na conta corrente.'}
                  </p>
                </button>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-muted/30 border space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Contas selecionadas:</span>
                <span className="font-semibold">{selecionadas.size}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total a pagar:</span>
                <span className="font-bold text-lg tabular-nums">{fmtCur(totalSelecionado)}</span>
              </div>
            </div>

            <div className="flex justify-between pt-3 border-t">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />Voltar
              </Button>
              <Button onClick={() => setStep(3)} disabled={!dtPagamento}>
                Próximo <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Passo 3: Revisão ─────────────────────────────────────────────── */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">3. Revisar Lote</CardTitle>
            <CardDescription>Confira os totais e avisos antes de confirmar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {carregandoPreview || !preview ? (
              <div className="text-center py-12">
                <Loader2 className="h-6 w-6 animate-spin inline-block" />
              </div>
            ) : (
              <>
                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <KpiCard label="Qtd Contas" value={preview.qtdContas} />
                  <KpiCard label="Total Geral" value={fmtCur(preview.totalGeral)} bold />
                  <KpiCard label="Vencidas" value={fmtCur(preview.totalVencido)} color="text-red-600" />
                  <KpiCard label="No Prazo / Futuras" value={fmtCur(preview.totalFuturo)} color="text-green-600" />
                </div>

                {/* Saldo após pagamento */}
                {preview.saldoConta !== null && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <KpiCard label="Saldo Atual da Conta" value={fmtCur(preview.saldoConta)} />
                    <KpiCard label="Total a Pagar" value={fmtCur(-preview.totalGeral)} color="text-red-600" />
                    <KpiCard label="Saldo Após" value={fmtCur(preview.saldoAposPagamento)}
                      color={(preview.saldoAposPagamento ?? 0) < 0 ? 'text-red-600' : 'text-green-600'} bold />
                  </div>
                )}

                {/* Avisos */}
                {preview.avisos.length > 0 && (
                  <div className="p-3 rounded-lg border-2 border-orange-300 bg-orange-50 dark:bg-orange-950/30 space-y-2">
                    <h4 className="font-semibold text-orange-700 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />Avisos
                    </h4>
                    <ul className="text-sm space-y-1 list-disc pl-5">
                      {preview.avisos.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                )}

                {/* Por fornecedor */}
                <div>
                  <h4 className="font-semibold text-sm mb-2">Agrupamento por Fornecedor</h4>
                  <div className="border rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background">
                        <TableRow>
                          <TableHead>Fornecedor</TableHead>
                          <TableHead className="text-center">Contas</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.porFornecedor.map((g) => (
                          <TableRow key={g.fornecedorId}>
                            <TableCell className="font-medium">{g.nomeFornecedor}</TableCell>
                            <TableCell className="text-center">{g.qtdContas}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">{fmtCur(g.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-between pt-3 border-t">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-2" />Voltar
              </Button>
              <Button onClick={() => setStep(4)} disabled={!preview}>
                Próximo <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Passo 4: Confirmação ─────────────────────────────────────────── */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">4. Confirmação Final</CardTitle>
            <CardDescription>
              {resultado ? 'Pagamento concluído.' : 'Revise tudo e clique em Confirmar para baixar as contas.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!resultado ? (
              <>
                <div className="p-4 rounded-lg border-2 border-primary bg-primary/5 space-y-2">
                  <p className="text-sm">
                    {modoExecucao === 'inter' && 'Você vai enviar para o Banco Inter:'}
                    {modoExecucao === 'cnab' && 'Será gerado um arquivo CNAB 240 com:'}
                    {modoExecucao === 'manual' && 'Você está prestes a baixar:'}
                  </p>
                  <p className="text-2xl font-bold tabular-nums">
                    {selecionadas.size} contas — {fmtCur(totalSelecionado)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {modoExecucao === 'inter' && '🏦 Pagamento via API Banco Inter'}
                    {modoExecucao === 'cnab' && '📄 Geração de arquivo .rem'}
                    {modoExecucao === 'manual' && '✓ Baixa interna no sistema'}
                    {' • '}{fmtDate(dtPagamento)}
                  </p>
                </div>

                <div className="flex justify-between pt-3 border-t">
                  <Button variant="outline" onClick={() => setStep(3)} disabled={executando}>
                    <ArrowLeft className="h-4 w-4 mr-2" />Voltar
                  </Button>
                  <Button onClick={executar} disabled={executando} size="lg">
                    {executando
                      ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processando...</>
                      : <><CheckCircle2 className="h-4 w-4 mr-2" />Confirmar Pagamento</>}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center py-8 space-y-4">
                  <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
                  <div>
                    <p className="text-2xl font-bold">Pagamento Concluído!</p>
                    <p className="text-muted-foreground">
                      {resultado.processadas} conta(s) baixadas — {fmtCur(resultado.totalPago)}
                    </p>
                  </div>
                </div>

                <div className="flex justify-center pt-3 border-t">
                  <Button onClick={reset}>Iniciar Novo Lote</Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KpiCard({
  label, value, color = 'text-foreground', bold,
}: { label: string; value: string | number; color?: string; bold?: boolean }) {
  return (
    <div className="p-3 rounded-lg border">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`tabular-nums ${bold ? 'text-2xl font-bold' : 'text-lg font-semibold'} ${color}`}>{value}</p>
    </div>
  );
}
